import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import classNames from 'classnames';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { NoteCreateTileType, TileType } from '@nx-frontend/shared/types';

const templateTiles: NoteCreateTileType[] = [
  TileType.ONBOARDING,
  TileType.WELCOME_PAGE,
  TileType.FAQ,
  TileType.FLOW_GUIDES,
];

export function PageTemplateSelect({
  changeTemplateId,
}: {
  changeTemplateId: (templateId: NoteCreateTileType | null) => void;
}) {
  const t = useTranslations('customizer');
  const [templateId, setTemplateId] = useState<NoteCreateTileType | null>(null);

  useEffect(() => {
    changeTemplateId(templateId);
  }, [changeTemplateId, templateId]);

  const getTemplateTitle = (tileType: TileType) => {
    switch (tileType) {
      case TileType.ONBOARDING:
        return t('customizer-onboarding');
      case TileType.WELCOME_PAGE:
        return t('customizer-welcome_page');
      case TileType.FAQ:
        return t('customizer-faq');
      case TileType.FLOW_GUIDES:
        return t('customizer-follow_block');
    }
  };

  const getTemplateDescription = (tileType: TileType) => {
    switch (tileType) {
      case TileType.ONBOARDING:
        return 'Design comprehensive onboarding experiences for your users';
      case TileType.WELCOME_PAGE:
        return 'Create an engaging and informative first point of contact';
      case TileType.FAQ:
        return 'Develop a clear and helpful frequently asked questions page';
      case TileType.FLOW_GUIDES:
        return 'Create detailed step-by-step guides and process instructions';
    }
  };

  const getTemplateIcon = (tileType: TileType): ReactElement | null => {
    switch (tileType) {
      case TileType.ONBOARDING:
        return IconsMap.USER_PLUS;
      case TileType.WELCOME_PAGE:
        return IconsMap.HOME;
      case TileType.FAQ:
        return IconsMap.QUESTION;
      case TileType.FLOW_GUIDES:
        return IconsMap.BOOKMARK;
      default:
        return null;
    }
  };

  const items = templateTiles.map((tile) => {
    return {
      id: tile,
      title: getTemplateTitle(tile),
      description: getTemplateDescription(tile),
      icon: getTemplateIcon(tile),
    };
  });
  return (
    <div className="flex flex-wrap gap-4" style={{ maxWidth: '33rem' }}>
      {items.map((item) => (
        <div
          key={item.id}
          onClick={() => setTemplateId(item.id)}
          className={classNames('p-4 flex w-64 flex-col gap-4 justify-center rounded-lg border cursor-pointer', {
            'border-neutral-200 hover:bg-neutral-100': templateId !== item.id,
            'border-cyan-900': templateId === item.id,
          })}
        >
          {item.icon && (
            <Icon
              icon={item.icon}
              className="size-10 p-2 rounded-lg border border-neutral-200 bg-white text-neutral-500"
            />
          )}
          <div>
            <h3 className="text-neutral-800 font-bold mb-2">{item.title}</h3>
            <p className="text-xs">{item.description}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
